import type {
  KeyboardEventHandler,
  MouseEventHandler,
  Ref,
  RefCallback,
} from 'react'
import React from 'react'
import classNames from 'classnames'

import type { TooltipOnHoverProps } from '../TooltipOnHover/TooltipOnHover'
import { TooltipOnHover } from '../TooltipOnHover/TooltipOnHover'
import { mergeRefs } from '../utils/mergeRefs'
import { handleSidebarRowNavigationKeys } from './keyboardNav'

import './styles.css'

const STANDARD_INDENTATION = 20

export type SidebarRowLayoutHoverZone = 'top' | 'middle' | 'bottom'

const CIRCLE_HEIGHT = 8
const LINE_HEIGHT = 2

const DragLine = ({ position }: { position: 'top' | 'bottom' }) => {
  const positionStyle = position === 'top' ? { top: -5 } : { bottom: -5 }
  return (
    <div
      className="position-absolute width-full flexbox flexbox-align-center zindex-small"
      style={{ lineHeight: 0, ...positionStyle }}
    >
      <div
        style={{ height: CIRCLE_HEIGHT, width: CIRCLE_HEIGHT }}
        className="border-medium border-primary radius-round"
      />
      <div
        style={{ height: LINE_HEIGHT }}
        className="width-full background-primary"
      />
    </div>
  )
}

export type SidebarRowLayoutRenderProps = {
  ref: RefCallback<HTMLElement>
  style: React.CSSProperties
  className: string
  tabIndex: number
  children: React.ReactElement
  onKeyDown: KeyboardEventHandler<HTMLElement>
  onContextMenu?: (event: React.MouseEvent) => void
  onClick?: (event: React.MouseEvent) => void
  'data-has-activity'?: boolean
}

function defaultSidebarRowLayoutRender(props: SidebarRowLayoutRenderProps) {
  return (
    <button
      type="button"
      {...props}
      className={classNames(
        'border-none background-transparent',
        props.className
      )}
    />
  )
}

function sidebarRowWithControlsRender({
  onKeyDown,
  ...props
}: SidebarRowLayoutRenderProps) {
  return (
    <div
      role="button"
      {...props}
      onKeyDown={(event) => {
        onKeyDown(event)
        const activates = event.key === 'Enter' || event.key === ' '
        if (activates && event.target === event.currentTarget) {
          event.preventDefault()
          event.currentTarget.click()
        }
      }}
    />
  )
}

export type SidebarRowLayoutProps = {
  ref?: Ref<HTMLElement>
  className?: string
  style?: React.CSSProperties
  depth?: number
  label: React.ReactNode
  /** When set, the row renders two lines: `label` on top, `subtitle` below. */
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  labelClassname?: string
  isActive?: boolean
  hasActivity?: boolean
  sticky?: boolean
  leftPart?: React.ReactNode
  rightPart?: React.ReactNode
  middlePart?: React.ReactNode
  isDragging?: boolean
  isDragOver?: boolean
  hoverZone?: SidebarRowLayoutHoverZone | null
  hidelabelOnHotjar?: boolean
  renderComponent?: (props: SidebarRowLayoutRenderProps) => React.ReactElement
  containsControls?: boolean
  onClick?: MouseEventHandler
  onContextMenu?: MouseEventHandler
  onKeyDown?: KeyboardEventHandler
  'data-test-id'?: string
  id?: string
  tooltipContent?: TooltipOnHoverProps['content']
  tooltipOpenDelay?: TooltipOnHoverProps['openDelay']
  labelSpanClassname?: string
}

export function SidebarRowLayout({
  className,
  style,
  depth = 0,
  label,
  subtitle,
  icon,
  labelClassname,
  labelSpanClassname,
  isActive,
  hasActivity,
  sticky,
  leftPart,
  rightPart,
  middlePart,
  isDragging,
  isDragOver,
  hoverZone,
  hidelabelOnHotjar,
  renderComponent,
  containsControls,
  onClick,
  onContextMenu,
  onKeyDown,
  tooltipContent,
  tooltipOpenDelay = 500,
  ref,
  ...props
}: SidebarRowLayoutProps) {
  const dropIn = isDragOver && hoverZone === 'middle'
  const dropHoverStyle = dropIn
    ? { boxShadow: '0 0 0 2px var(--primary) inset' }
    : {}
  const hasSubtitle = subtitle != null
  const render =
    renderComponent ??
    (containsControls
      ? sidebarRowWithControlsRender
      : defaultSidebarRowLayoutRender)

  return (
    <TooltipOnHover
      cardClassName="flexbox-grow ellipsis"
      content={tooltipContent}
      disabled={!tooltipContent}
      positions={['right', 'bottom']}
      openDelay={tooltipOpenDelay}
      cardStyle={{ maxWidth: '420px' }}
      target={({ props: targetProps, ref: tooltipRef }) =>
        render({
          ref: mergeRefs(ref, tooltipRef),
          className: classNames(
            'sidebar-row',
            'flexbox flexbox-align-center flexbox-no-grow flexbox-no-shrink',
            'position-relative radius-large no-outline pointer',
            {
              'sidebar-row--selected': isActive,
              'opacity-smaller': isDragging,
            },
            className
          ),
          style: {
            ...dropHoverStyle,
            marginLeft: depth > 0 ? STANDARD_INDENTATION * depth : 0,
            position: sticky ? 'sticky' : undefined,
            top: sticky ? 0 : undefined,
            zIndex: sticky ? 1 : undefined,
            marginBottom: 1,
            minHeight: 32,
            paddingLeft: leftPart ? 6 : 8,
            paddingRight: 8,
            ...style,
          },
          onClick,
          onContextMenu,
          onKeyDown: onKeyDown
            ? (event) => {
                handleSidebarRowNavigationKeys(event)
                onKeyDown(event)
              }
            : handleSidebarRowNavigationKeys,
          tabIndex: 0,
          'data-has-activity': hasActivity || undefined,
          children: (
            <>
              {leftPart && (
                <div
                  className="flexbox-no-shrink flexbox flexbox-centered"
                  style={{ width: 20 }}
                >
                  {leftPart}
                </div>
              )}
              {icon ? (
                <div
                  className={classNames(
                    'flexbox-no-shrink flexbox flexbox-centered',
                    {
                      'shade-05': hasActivity,
                      'shade-20': !hasActivity,
                    }
                  )}
                  style={{ width: 20, height: 20, marginRight: 6 }}
                >
                  {icon}
                </div>
              ) : (
                <div style={{ height: 20 }} />
              )}
              <div
                {...targetProps}
                className={classNames(
                  'sidebar-row-text flexbox ellipsis font-size-large',
                  hasSubtitle
                    ? 'flexbox-column flexbox-gap-4 min-width-zero'
                    : 'flexbox-align-center',
                  labelClassname,
                  {
                    'fs-block data-hj-suppress': hidelabelOnHotjar,
                    'sidebar-row-text--has-activity': hasActivity,
                    'sidebar-row-text--without-activity': !hasActivity,
                    'flexbox-grow': !middlePart,
                  }
                )}
                data-test-id={props['data-test-id'] || 'sidebarItem'}
              >
                <span className={classNames('ellipsis', labelSpanClassname)}>
                  {label}
                </span>
                {hasSubtitle && (
                  <span className="font-size-small shade-30 ellipsis">
                    {subtitle}
                  </span>
                )}
              </div>
              {middlePart && (
                <div className="flexbox-no-shrink flexbox-grow">
                  {middlePart}
                </div>
              )}
              {rightPart}
              {isDragOver && hoverZone === 'top' && <DragLine position="top" />}
              {isDragOver && hoverZone === 'bottom' && (
                <DragLine position="bottom" />
              )}
            </>
          ),
        })
      }
    />
  )
}
